import { Component, HostListener, ViewChild, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { IAppDataModel, IYearData, YearData } from '@app/@appstate/appdata.model';

import { AppDataQuery } from '@app/@appstate/appdata.query';
import { AppDataService } from '@app/@appstate/appdata.service';
import { AuthQuery } from '@app/auth/auth.query';
import { AuthDataService } from '@app/auth/auth-data.service';

import { Observable } from 'rxjs';

import { ConfirmationService } from 'primeng/api';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatDialog, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';

import { JournalService } from '@app/@core';
import {
  StammdatenService,
  Artikel,
  ArtikelStammdaten,
  Massnahme,
  MobileTextTemplate,
} from '@app/services/stammdaten.service';

import { DialogInputComponent } from '../@shared/dialog/input/dialog-input.component';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';

import { ArtikelDialogComponent } from './dialog-artikel/dialog-artikel.component';
import { DialogMassnahmeComponent } from './dialog-massnahme/dialog-massnahme.component';
import { DialogMobileTextbausteinComponent } from './dialog-mobile-textbaustein/dialog-mobile-textbaustein.component';

// data structures
interface ArtikelExt extends Artikel {
  isEditable?: boolean;
}

interface MassnahmeExt extends Massnahme {
  isEditable?: boolean;
}

interface MobileTextTemplateExt extends MobileTextTemplate {
  isEditable?: boolean;
}

/**
 * component
 *
 */
@Component({
  selector: 'app-stammdaten',
  templateUrl: './stammdaten.component.html',
  styleUrls: ['./stammdaten.component.css'],
})
export class StammdatenComponent implements OnInit {
  // the state
  appData: IAppDataModel;

  // copy of the current state
  cAppData: IAppDataModel;

  arMonate = [];

  changePwdForm!: UntypedFormGroup;

  // Display columns
  displayArtikel: string[] = ['nummer', 'beschreibung', 'art', 'basiseinheit', 'code', 'status', 'action'];
  displayMassnahme: string[] = ['beschreibung', 'druck_auf_bericht', 'foto_erforderlich', 'sortierung', 'status', 'action'];
  displayTextbaustein: string[] = ['schluesselwort', 'text', 'sortierung', 'status', 'action'];
  
  // Data sources
  artikelStammdaten: ArtikelStammdaten;
  dataSourceArtikel: MatTableDataSource<ArtikelExt> = new MatTableDataSource<ArtikelExt>();
  dataSourceMassnahme: MatTableDataSource<MassnahmeExt> = new MatTableDataSource<MassnahmeExt>();
  dataSourceTextbaustein: MatTableDataSource<MobileTextTemplateExt> = new MatTableDataSource<MobileTextTemplateExt>();
  
  // Filter states
  artikelFilterStatus: 'all' | 'active' | 'inactive' = 'active';
  massnahmeFilterStatus: 'all' | 'active' | 'inactive' = 'active';
  textbausteinFilterStatus: 'all' | 'active' | 'inactive' = 'active';
  artikelFilterOpen: boolean = false;
  massnahmeFilterOpen: boolean = false;
  textbausteinFilterOpen: boolean = false;
  
  // Original data (before filtering)
  allArtikels: ArtikelExt[] = [];
  allMassnahmen: MassnahmeExt[] = [];
  allTextbausteine: MobileTextTemplateExt[] = [];

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _adQuery: AppDataQuery,
    private _adService: AppDataService,
    private authQuery: AuthQuery,
    private authDataService: AuthDataService,
    private stammdatenService: StammdatenService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private journalService: JournalService
  ) {}

  ngOnInit(): void {
    // query and init the data
    this._adQuery.selectEntity(this.authQuery.appid).subscribe((state) => {
      this.appData = <IAppDataModel>state;

      // deep clone
      try {
        this.cAppData = JSON.parse(JSON.stringify(state));
      } catch (err) {
        // console.log("error");
      }

      //console.log("change state");
    });

    // get the stammdaten
    this.getArtikelStammdaten();
    this.getArtikels();
    this.getMassnahmen();
    this.getTextbausteine();
    // Abweichungen tab removed - no longer needed
  }

  /**
   * get the artikel stammdaten
   */
  getArtikelStammdaten() {
    console.log('Loading artikel stammdaten...');
    this.stammdatenService.getArtikelStammdaten().subscribe({
      next: (response) => {
        console.log('Artikel stammdaten loaded:', response);
        this.artikelStammdaten = response;
      },
      error: (error) => {
        console.error('Error loading artikel stammdaten:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Artikel-Stammdaten', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Get all artikels (actual artikel records)
   */
  getArtikels() {
    console.log('Loading artikels...');
    this.stammdatenService.getArtikels().subscribe({
      next: (response) => {
        console.log('Artikels loaded:', response);
        console.log('Artikels data length:', response?.length);
        this.allArtikels = response || [];
        this.applyArtikelFilter();
      },
      error: (error) => {
        console.error('Error loading artikels:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Artikel', 'Schließen', { duration: 3000 });
      }
    });
  }
  
  /**
   * Apply filter to artikels based on status_id (0 = inactive, 1 = active)
   */
  applyArtikelFilter() {
    let filtered = [...this.allArtikels];
    
    // Debug: Log status_id values to see what we're working with
    if (this.allArtikels.length > 0) {
      const statusIds = this.allArtikels.map(item => item.status_id);
      console.log('Artikel status_id values:', [...new Set(statusIds)]);
      console.log('Sample artikel:', this.allArtikels[0]);
    }
    
    if (this.artikelFilterStatus === 'active') {
      // Show only active items (status_id === 1)
      filtered = filtered.filter(item => item.status_id === 1);
      console.log('Filtered to active artikels:', filtered.length);
    } else if (this.artikelFilterStatus === 'inactive') {
      // Show only inactive items (status_id !== 1, which includes 0, null, undefined, etc.)
      filtered = filtered.filter(item => item.status_id !== 1);
      console.log('Filtered to inactive artikels:', filtered.length, 'from total:', this.allArtikels.length);
      console.log('Inactive artikels sample:', filtered.slice(0, 3).map(item => ({ id: item.id, status_id: item.status_id })));
    }
    // 'all' shows everything, no filtering needed
    
    this.dataSourceArtikel.data = filtered;
  }
  
  /**
   * Change artikel filter status
   */
  onArtikelFilterChange(status: 'all' | 'active' | 'inactive') {
    this.artikelFilterStatus = status;
    this.applyArtikelFilter();
  }

  /**
   * sync artikel stammdaten
   */
  syncArtikelStammdaten() {
    console.log('Syncing artikel stammdaten...');
    this.stammdatenService.syncArtikelStammdaten(this.artikelStammdaten).subscribe({
      next: (response) => {
        console.log('Artikel stammdaten synced:', response);
        this.snackBar.open('Artikel-Stammdaten erfolgreich gespeichert', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error syncing artikel stammdaten:', error);
        this.snackBar.open('Fehler beim Speichern der Artikel-Stammdaten', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * add new artikel
   */
  addNewArtikel() {
    const dialogRef = this.dialog.open(ArtikelDialogComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Produkt saved:', result);
        this.getArtikels(); // Reload data
      }
    });
  }

  /**
   * edit item
   */
  editItem(type: string, item: any) {
    if (type === 'Produkt' || type === 'Artikel') {
      const dialogRef = this.dialog.open(ArtikelDialogComponent, {
        width: '600px',
        data: { mode: 'edit', artikel: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          console.log('Produkt updated:', result);
          this.getArtikels(); // Reload data
        }
      });
    } else if (type === 'Massnahme') {
      const dialogRef = this.dialog.open(DialogMassnahmeComponent, {
        width: '600px',
        data: { mode: 'edit', massnahme: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          console.log('Massnahme updated:', result);
          this.getMassnahmen();
        }
      });
    } else if (type === 'Textbaustein') {
      const dialogRef = this.dialog.open(DialogMobileTextbausteinComponent, {
        width: '600px',
        data: { mode: 'edit', template: item }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getTextbausteine();
        }
      });
    }
  }

  /**
   * remove artikel item (soft delete - set inactive)
   */
  removeItem(type: string, item: any) {
    if (type === 'Produkt' || type === 'Artikel') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie dieses Produkt deaktivieren möchten?',
        accept: () => {
          this.stammdatenService.deleteArtikel(item.id).subscribe({
            next: () => {
              this.snackBar.open('Produkt erfolgreich deaktiviert', 'Schließen', { duration: 3000 });
              this.getArtikels();
            },
            error: (error) => {
              console.error('Error deactivating artikel:', error);
              this.snackBar.open('Fehler beim Deaktivieren des Produkts', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    } else if (type === 'Massnahme') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie diese Maßnahme deaktivieren möchten?',
        accept: () => {
          this.stammdatenService.deleteMassnahme(item.id).subscribe({
            next: () => {
              this.snackBar.open('Maßnahme erfolgreich deaktiviert', 'Schließen', { duration: 3000 });
              this.getMassnahmen();
            },
            error: (error) => {
              console.error('Error deactivating massnahme:', error);
              this.snackBar.open('Fehler beim Deaktivieren der Maßnahme', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    } else if (type === 'Textbaustein') {
      this.confirmationService.confirm({
        message: 'Sind Sie sicher, dass Sie diesen Textbaustein deaktivieren möchten?',
        accept: () => {
          this.stammdatenService.deleteMobileTextTemplate(item.id).subscribe({
            next: () => {
              this.snackBar.open('Textbaustein erfolgreich deaktiviert', 'Schließen', { duration: 3000 });
              this.getTextbausteine();
            },
            error: (error) => {
              console.error('Error deactivating text template:', error);
              this.snackBar.open('Fehler beim Deaktivieren des Textbausteins', 'Schließen', { duration: 3000 });
            }
          });
        }
      });
    }
  }

  /**
   * Get all massnahmen
   */
  getMassnahmen() {
    console.log('Loading massnahmen...');
    this.stammdatenService.getMassnahmen().subscribe({
      next: (response) => {
        console.log('Massnahmen loaded:', response);
        console.log('Massnahmen data length:', response?.length);
        this.allMassnahmen = response || [];
        this.applyMassnahmeFilter();
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Maßnahmen', 'Schließen', { duration: 3000 });
      }
    });
  }
  
  /**
   * Apply filter to massnahmen based on status_id (0 = inactive, 1 = active)
   */
  applyMassnahmeFilter() {
    let filtered = [...this.allMassnahmen];
    
    // Debug: Log status_id values to see what we're working with
    if (this.allMassnahmen.length > 0) {
      const statusIds = this.allMassnahmen.map(item => item.status_id);
      console.log('Massnahme status_id values:', [...new Set(statusIds)]);
      console.log('Sample massnahme:', this.allMassnahmen[0]);
    }
    
    if (this.massnahmeFilterStatus === 'active') {
      // Show only active items (status_id === 1)
      filtered = filtered.filter(item => item.status_id === 1);
      console.log('Filtered to active massnahmen:', filtered.length);
    } else if (this.massnahmeFilterStatus === 'inactive') {
      // Show only inactive items (status_id !== 1, which includes 0, null, undefined, etc.)
      filtered = filtered.filter(item => item.status_id !== 1);
      console.log('Filtered to inactive massnahmen:', filtered.length, 'from total:', this.allMassnahmen.length);
      console.log('Inactive massnahmen sample:', filtered.slice(0, 3).map(item => ({ id: item.id, status_id: item.status_id })));
    }
    // 'all' shows everything, no filtering needed
    
    this.dataSourceMassnahme.data = filtered;
  }
  
  /**
   * Change massnahme filter status
   */
  onMassnahmeFilterChange(status: 'all' | 'active' | 'inactive') {
    this.massnahmeFilterStatus = status;
    this.applyMassnahmeFilter();
  }

  /**
   * Add new massnahme
   */
  addNewMassnahme() {
    const dialogRef = this.dialog.open(DialogMassnahmeComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Massnahme saved:', result);
        this.getMassnahmen();
      }
    });
  }

  /**
   * Get all mobile text templates (Textbausteine), including inactive for management
   */
  getTextbausteine() {
    this.stammdatenService.getMobileTextTemplates(true).subscribe({
      next: (response) => {
        this.allTextbausteine = response || [];
        this.applyTextbausteinFilter();
      },
      error: (error) => {
        console.error('Error loading text templates:', error);
        this.snackBar.open('Fehler beim Laden der Textbausteine', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Apply filter to text templates based on status_id (0 = inactive, 1 = active)
   */
  applyTextbausteinFilter() {
    let filtered = [...this.allTextbausteine];

    if (this.textbausteinFilterStatus === 'active') {
      filtered = filtered.filter(item => item.status_id === 1);
    } else if (this.textbausteinFilterStatus === 'inactive') {
      filtered = filtered.filter(item => item.status_id !== 1);
    }

    this.dataSourceTextbaustein.data = filtered;
  }

  /**
   * Change text template filter status
   */
  onTextbausteinFilterChange(status: 'all' | 'active' | 'inactive') {
    this.textbausteinFilterStatus = status;
    this.applyTextbausteinFilter();
  }

  /**
   * Add new text template
   */
  addNewTextbaustein() {
    const dialogRef = this.dialog.open(DialogMobileTextbausteinComponent, {
      width: '600px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getTextbausteine();
      }
    });
  }

  /**
   * Close filter dropdowns when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.relative')) {
      this.artikelFilterOpen = false;
      this.massnahmeFilterOpen = false;
      this.textbausteinFilterOpen = false;
    }
  }

  /**
   * Get status badge classes based on status_id
   */
  getStatusClasses(statusId: number): string {
    if (statusId === 1) {
      return 'bg-green-50 text-green-700 ring-green-600/20';
    } else {
      return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  }

  /**
   * Get status description based on status_id
   */
  getStatusDescription(statusId: number): string {
    return statusId === 1 ? 'Aktiv' : 'Inaktiv';
  }
}
